import { useEffect, useState } from "react";
import { Eye, KeyRound, Sparkles, CalendarDays } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";

const Bars = ({ items, empty }) => {
  const max = Math.max(1, ...items.map((i) => i.count));
  if (!items.length) return <p className="text-sm text-brand-forest/50">{empty}</p>;
  return (
    <div className="space-y-2.5">
      {items.map((i) => (
        <div key={i.label} data-testid={`bar-${i.label}`} className="flex items-center gap-3">
          <span className="w-32 shrink-0 text-sm text-brand-forest/80 truncate">{i.label}</span>
          <div className="flex-1 h-6 rounded-full bg-brand-sand overflow-hidden">
            <div className="h-full rounded-full bg-brand-terracotta transition-all" style={{ width: `${(i.count / max) * 100}%` }} />
          </div>
          <span className="w-10 text-right text-sm font-semibold text-brand-forest">{i.count}</span>
        </div>
      ))}
    </div>
  );
};

const fmtDate = (iso) => {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch { return iso; }
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [selDay, setSelDay] = useState("");

  useEffect(() => { api.get("/analytics/summary").then((r) => { setData(r.data); if (r.data?.daily_pages?.length) setSelDay(r.data.daily_pages[0].date); }).catch(() => {}); }, []);

  if (!data) return <p className="text-brand-forest/60">Loading analytics…</p>;

  const stats = [
    { label: "Total page views", value: data.total_views, icon: Eye },
    { label: "Door-code unlocks", value: data.door_unlocks, icon: KeyRound },
    { label: "Add-on requests", value: data.addon_requests.reduce((s, a) => s + a.count, 0), icon: Sparkles },
  ];
  const daily = data.daily.map((d) => ({ ...d, label: fmtDate(d.date) }));
  const dailyPages = data.daily_pages || [];
  const selDayData = dailyPages.find((d) => d.date === selDay);

  return (
    <div data-testid="analytics-page">
      <div className="mb-6"><span className="overline text-brand-terracotta">Insights</span><h1 className="font-display text-4xl tracking-tight mt-1">Analytics</h1>
        <p className="text-brand-forest/60 mt-1">Private guest activity — page views, unlocks and add-on interest, including what each guest looked at during their stay.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} data-testid={`stat-${s.label}`} className="bg-white rounded-2xl p-5 border border-black/5 shadow-sm">
              <Icon size={20} className="text-brand-terracotta" />
              <p className="font-display text-4xl tracking-tight mt-3">{s.value}</p>
              <p className="text-sm text-brand-forest/60 mt-1">{s.label}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm mb-8">
        <h2 className="font-display text-2xl tracking-tight mb-4">Views over time</h2>
        {daily.length ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={daily} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e2d8" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#2C3B2E" }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#2C3B2E" }} />
              <Tooltip cursor={{ fill: "rgba(196,112,83,0.08)" }} contentStyle={{ borderRadius: 12, border: "1px solid #eee" }} />
              <Bar dataKey="count" fill="#C47053" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <p className="text-sm text-brand-forest/50">No views recorded yet.</p>}
      </div>

      <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm mb-8" data-testid="daily-pages-section">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <h2 className="font-display text-2xl tracking-tight">Pages viewed on a specific day</h2>
          <select
            data-testid="daily-pages-date"
            value={selDay}
            onChange={(e) => setSelDay(e.target.value)}
            className="rounded-full border border-black/10 px-4 py-2 text-sm bg-white focus:outline-none focus:border-brand-terracotta"
          >
            {dailyPages.map((d) => (
              <option key={d.date} value={d.date}>{fmtDate(d.date)} — {d.total} view{d.total === 1 ? "" : "s"}</option>
            ))}
          </select>
        </div>
        {selDayData ? (
          <Bars items={selDayData.pages.map((p) => ({ label: p.page, count: p.count }))} empty="No page views that day." />
        ) : <p className="text-sm text-brand-forest/50">No daily page data yet.</p>}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm">
          <h2 className="font-display text-2xl tracking-tight mb-4">Views per page</h2>
          <Bars items={data.totals.map((t) => ({ label: t.page, count: t.count }))} empty="No page views yet." />
        </div>
        <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm">
          <h2 className="font-display text-2xl tracking-tight mb-4">Add-on requests</h2>
          <Bars items={data.addon_requests.map((a) => ({ label: a.label, count: a.count }))} empty="No add-on requests yet." />
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm">
        <h2 className="font-display text-2xl tracking-tight mb-1">Views during each booking</h2>
        <p className="text-sm text-brand-forest/60 mb-5">What each guest looked at while they were staying.</p>
        {data.bookings.length ? (
          <div className="space-y-4">
            {data.bookings.map((b) => (
              <div key={b.reservation_id} data-testid={`booking-analytics-${b.reservation_id}`} className="rounded-xl border border-black/5 p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <CalendarDays size={16} className="text-brand-terracotta" />
                    <span className="font-display text-lg tracking-tight">{b.guest_name}</span>
                    <span className="text-sm text-brand-forest/50">{b.checkin} → {b.checkout}</span>
                  </div>
                  <span className="text-sm font-semibold text-brand-forest">{b.total} views</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {b.pages.map((p) => (
                    <span key={p.page} className="inline-flex items-center gap-1.5 rounded-full bg-brand-sand px-3 py-1 text-sm">
                      {p.page} <span className="font-semibold text-brand-terracotta">{p.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-brand-forest/50">No booking-linked views yet. Views are tagged to a booking automatically when a guest browses during their stay window.</p>}
      </div>
    </div>
  );
}
